import React, { useRef, useEffect } from 'react';
import { css } from '@emotion/core';
import i18next from 'i18next';
import { error, functional, neutral300, neutral0 } from '@fever/ui';

const OtpInputGroup = ({
    values,
    onChange,
    error = false,
    isLoading,
    disabled = false,
    style,
    onComplete = () => {}
}) => {
    const inputRefs = useRef([]);

    useEffect(() => {
        // Auto-focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    useEffect(() => {
        // Check for auto-filled values using requestAnimationFrame with throttling
        let isCheckingAutoFill = true;
        let lastCheckTime = 0;
        const CHECK_INTERVAL = 100; // Throttle to 100ms intervals

        const checkAutoFill = () => {
            const inputValues = inputRefs.current.map(input => input?.value || '');
            const stateValues = values;

            // Check if DOM values differ from state values (indicating auto-fill)
            const hasAutoFill = inputValues.some(
                (domValue, index) => domValue !== stateValues[index] && domValue !== ''
            );

            if (hasAutoFill) {
                // Update state to match DOM values
                const newValues = inputValues.slice(0, 6);
                onChange(newValues);

                // Check if all fields are filled and trigger onComplete
                if (newValues.every(val => val !== '') && newValues.length === 6) {
                    onComplete(newValues.join(''));
                    isCheckingAutoFill = false; // Stop checking once complete
                    return;
                }
            }
        };

        const animationFrameCheck = currentTime => {
            if (!isCheckingAutoFill) return;

            // Throttle checks to avoid excessive calls
            if (currentTime - lastCheckTime >= CHECK_INTERVAL) {
                checkAutoFill();
                lastCheckTime = currentTime;
            }

            // Continue checking
            requestAnimationFrame(animationFrameCheck);
        };

        // Start checking with initial delay
        const initialTimeout = setTimeout(() => {
            if (isCheckingAutoFill) {
                requestAnimationFrame(animationFrameCheck);
            }
        }, 50);

        return () => {
            isCheckingAutoFill = false;
            clearTimeout(initialTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    // Reset focus to first input when error state changes
    useEffect(() => {
        if (error && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [error]);

    const handleInputChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        // Update the values array
        const newValues = [...values];
        newValues[index] = value;
        onChange(newValues);

        // Auto-advance to next input if value entered
        if (value && index < 5) {
            const nextInput = inputRefs.current[index + 1];
            if (nextInput) {
                nextInput.focus();
            }
        }

        // Check if all fields are filled and trigger onComplete
        if (newValues.every(val => val !== '')) {
            onComplete(newValues.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            if (values[index] === '') {
                // If current field is empty, move to previous and clear it
                if (index > 0) {
                    const newValues = [...values];
                    newValues[index - 1] = '';
                    onChange(newValues);
                    const prevInput = inputRefs.current[index - 1];
                    if (prevInput) {
                        prevInput.focus();
                    }
                }
            } else {
                // If current field has value, just clear it
                const newValues = [...values];
                newValues[index] = '';
                onChange(newValues);
            }
        }

        // Handle arrow keys
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1].focus();
        }
        if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handlePaste = e => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');

        // Only process if it's exactly 6 digits
        if (/^\d{6}$/.test(pastedData)) {
            const newValues = pastedData.split('');
            onChange(newValues);

            // Update each input field directly for better test compatibility
            newValues.forEach((digit, index) => {
                if (inputRefs.current[index]) {
                    inputRefs.current[index].value = digit;
                }
            });

            // Focus the last input
            const lastInput = inputRefs.current[5];
            if (lastInput) {
                lastInput.focus();
            }

            // Trigger onComplete
            onComplete(pastedData);
        }
    };

    return (
        <div css={[containerStyle(isLoading, disabled), css(style)]} data-testid="otp-input-group">
            {values.map((value, index) => (
                <input
                    key={index}
                    ref={el => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={value}
                    onChange={e => handleInputChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    css={[inputStyle, error && errorStyle, disabled && disabledStyle]}
                    aria-label={`${i18next.t('placeholder.verification_code_input_digit', {
                        key: index + 1
                    })}`}
                    autoComplete="one-time-code"
                    disabled={isLoading || disabled}
                    data-testid={`otp-input-${index + 1}`}
                />
            ))}
        </div>
    );
};

const containerStyle = (isLoading, disabled) => css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    ${disabled &&
    css`
        opacity: 0.5;
        pointer-events: none;
        cursor: not-allowed;
    `}

    ${isLoading &&
    css`
        opacity: 0.5;
        pointer-events: none;
    `}

    @media (max-width: 480px) {
        gap: 6px;
        padding: 0 12px;
    }

    @media (max-width: 360px) {
        gap: 4px;
        padding: 0 8px;
    }
`;

const inputStyle = css`
    width: 48px;
    height: 56px;
    border-radius: 4px;
    border: 1px solid ${neutral300};
    background: ${neutral0};
    font-size: 24px;
    font-weight: 500;
    text-align: center;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: ${functional};
    }

    &:hover {
        border-color: ${functional};
    }

    @media (max-width: 480px) {
        width: 42px;
        height: 50px;
        font-size: 20px;
    }

    @media (max-width: 360px) {
        width: 36px;
        height: 44px;
        font-size: 18px;
    }
`;

const errorStyle = css`
    border-color: ${error};

    &:focus,
    &:hover {
        border-color: ${error};
    }
`;

const disabledStyle = css`
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
`;

export default OtpInputGroup;
